import styled from "styled-components";

import {Typography, type Variant,} from "@bracket/ui";
import { type FunctionComponent, useEffect, useState, } from "react";
import { assetToUsd, } from "@utils";
import type { WEIString, } from "@bracket/types";
import type { LoadedVault, } from "@types";
import { getTheme, } from "@bracket/ui/themes";


/**
 * Props for the AssetDollars component
 * @property {WEIString | bigint} amount - Amount in Asset (WEI string or bigint)
 * @property {number} price - USD price of 1 Asset
 * @property {Variant} [variant] - Typography variant to use
 * @property {number} [precision] - Number of decimal places to show for Asset amount
 * @property {boolean} [label] - Whether to show the asset label
 * @property {"reverse"} [order] - If "reverse", shows Asset amount before USD value
 * @property {boolean} [multiline] - Whether to display values on multiple lines
 */
export declare type AssetDollarsProps = {
	amount: WEIString | bigint;
	price?: number;
	variant?: Variant;
	precision?: number;
	label?: boolean;
	order?: "reverse";
	multiline?: boolean;
	cents?: boolean;
	denominator?: LoadedVault["denomination"];
	hideDollars?: boolean;
	hideAsset?: boolean;
	hideAssetIfUSDC?: boolean;
	showDashForZero?: boolean;
	dollarSize?: string;
	dollarLineHeight?: string;
	dollarFont?: string;
	dollarWeight?: string | number;
	assetSize?: string;
	assetLineHeight?: string;
	className?: string;
};




/**
 * AssetDollars component displays an amount in both asset and USD values
 * 
 * @param {WEIString | bigint} amount - Amount in Asset (WEI string or bigint)
 * @param {number} price - USD price of 1 Asset
 * @param {Variant} [variant="p"] - Typography variant to use
 * @param {number} [precision=5] - Number of decimal places to show for Asset amount
 * @param {boolean} [label] - Whether to show the asset label
 * @param {"reverse"} [order] - If "reverse", shows Asset amount before USD value
 * @param {boolean} [multiline] - Whether to display values on multiple lines
 * @param {boolean} [cents=true] - Whether to show cents in USD value
 * @param {LoadedVault["denomination"]} [denominator] - Denomination details for the asset
 * @param {boolean} [hideDollars=false] - Whether to hide the USD value
 * @param {boolean} [hideAsset=false] - Whether to hide the asset value
 * @returns {JSX.Element | null} Rendered component or null if amount is invalid */
export const AssetDollars: FunctionComponent<AssetDollarsProps> = ({
	amount,
	price,
	variant = "textSM",
	precision = 5,
	label = true,
	order,
	multiline,
	cents = true,
	denominator,
	hideDollars = false,
	hideAsset = false,
	hideAssetIfUSDC = true,
	showDashForZero = false,
	dollarSize = "1rem",
	dollarLineHeight = "1rem",
	dollarFont = "Space Grotesk",
	dollarWeight = 500,
	assetSize = "0.8rem",
	assetLineHeight = "1rem",
	className,
}) => {


	
	const bAmount = typeof amount === "bigint" ? amount : BigInt(Math.floor(Number(amount)));
	const iAmount = Number(bAmount) / (10**(denominator?.decimals||18));
	const hideBecauseUsdc = hideAssetIfUSDC && denominator?.symbol.toUpperCase() === "USDC";
	const [calcPrice, setCalcPrice,] = useState<number>(typeof price === "number" ? price : -9999);

	useEffect(() => {
		( async () => {
			setCalcPrice(await assetToUsd(amount.toString(), denominator || { symbol: "BRKTETH", chainId: 1, }));
			return;
		})();
	}, [ price, amount, ]);


	const assetValue = () => {
		return iAmount.toFixed(precision);
	}

	const dollarValue = () => {
		const price = calcPrice === -9999 ? 0 : calcPrice;
		const result = price.toLocaleString("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: cents ? 2 : 0,
			maximumFractionDigits: cents ? 2 : 0,
		});
		return result;
	}

	if(typeof amount !== "bigint" && !amount.length) {
		return null;
	}

	return (
		<Brace $assetSize={assetSize} $assetLineHeight={assetLineHeight} $dollarSize={dollarSize} $dollarLineHeight={dollarLineHeight} $dollarFont={dollarFont} $dollarWeight={dollarWeight} $multiline={multiline} className={className}>
			<Typography variant={variant}>
				{order === "reverse" ? (
					iAmount === 0 && showDashForZero ? "-" : (
						<AssetBrace $multiline={multiline}>
							<span className="asset">
								{!hideAsset && !hideBecauseUsdc && assetValue()}
								{!hideAsset && !hideBecauseUsdc && label ? ` ${denominator?.label || "brktETH"}` : ""}
							</span>
							<span className="dollars">{!hideDollars && !hideBecauseUsdc && dollarValue()}</span>
						</AssetBrace>
					)
				) : (
					iAmount === 0 && showDashForZero ? "-" : (
						<AssetBrace $multiline={multiline}>	
							<span className="dollars" style={{ fontSize: dollarSize, lineHeight: dollarLineHeight }}>{!hideDollars && dollarValue()}</span>
							<span className="asset">
								{!hideAsset && !hideBecauseUsdc && assetValue()}
								{!hideAsset && !hideBecauseUsdc && label ? ` ${denominator?.label || "brktETH"}` : ""}
							</span>
						</AssetBrace>
					)
				)}
			</Typography>
		</Brace>
	);
};



const Brace = styled.div<{ $assetSize: string; $assetLineHeight: string; $dollarSize: string; $dollarLineHeight: string; $dollarFont: string; $dollarWeight: string | number; $multiline?: boolean; }>`
	display: flex;
	flex-direction: row;
	white-space: nowrap;
	align-items: center;
	gap: 0.5rem;
  p {
		display: flex;
		gap: 0.25rem;
	}
	.asset {
		color: ${() => getTheme().subheading};
		font-size: ${({ $assetSize }) => $assetSize};
		line-height: ${({ $assetLineHeight }) => $assetLineHeight};
		margin-top: ${({ $multiline }) => $multiline ? ".5rem" : "1px"};
		margin-left: ${({ $multiline }) => $multiline ? "0" : "3px"};
		font-family: Inter;
	}
	.dollars {
		font-size: ${({ $dollarSize }) => $dollarSize};
		line-height: ${({ $dollarLineHeight }) => $dollarLineHeight};
		font-family: ${({ $dollarFont }) => $dollarFont};
		font-style: normal;
		font-weight: ${({ $dollarWeight }) => $dollarWeight};
	}
`;


const AssetBrace = styled.div<{ $multiline?: boolean }>`
	display: flex;
	flex-direction: ${({ $multiline }) => $multiline ? "column" : "row"};
	> span {
		display: flex;
		align-items: center;
		justify-content: center;
	}
`;
